import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  IconButton,
  TextField,
  Button,
  Divider,
  Select,
  MenuItem,
  useTheme,
  Chip
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import AttachFileIcon from '@mui/icons-material/AttachFile'
import MailOutlineIcon from '@mui/icons-material/MailOutline'
import PhoneIcon from '@mui/icons-material/Phone'
import ChatMessage from './ChatMessage'
import CustomLoader from '../Loader/CustomLoader'
import { SUPPORT_TICKET_STATUS } from '../../utils/enums'
import { useQuery, useMutation, gql } from '@apollo/client'
import { getTicketMessages } from '../../apollo/queries'
import ConfigurableValues from '../../config/constants'
import useFileToBase64 from '../../hooks/useFileToBase64'
import {
  createMessage,
  updateSupportTicketStatus
} from '../../apollo/mutations'

const GET_TICKET_MESSAGES = gql`
  ${getTicketMessages}
`
const CREATE_MESSAGE = gql`
  ${createMessage}
`
const UPDATE_TICKET_STATUS = gql`
  ${updateSupportTicketStatus}
`

const TicketChatModal = ({ open, onClose, ticketId }) => {
  const theme = useTheme()
  const fileInputRef = useRef(null)
  const messagesContainerRef = useRef(null)
  const { CLOUDINARY_UPLOAD_URL, CLOUDINARY_FOOD } = ConfigurableValues()
  const fileToBase64 = useFileToBase64()
  const [message, setMessage] = useState('')
  const [attachments, setAttachments] = useState([])
  const [uploadingAttachments, setUploadingAttachments] = useState(false)
  const [uploadError, setUploadError] = useState('')
  const messagesEndRef = useRef(null)

  const { data, loading, error, refetch } = useQuery(GET_TICKET_MESSAGES, {
    variables: { input: { ticket: ticketId, page: 1, limit: 100 } },
    skip: !ticketId,
    pollInterval: 5000
  })

  const [mutateMessage] = useMutation(CREATE_MESSAGE)
  const [mutateStatus] = useMutation(UPDATE_TICKET_STATUS)

  const ticket = data?.getTicketMessages?.ticket

  // The message that opened the ticket lives on the ticket itself, as its
  // description -- raising a ticket never writes a TicketMessage. Without it
  // prepended the thread starts at the first reply, so the customer's original
  // complaint is missing from every conversation.
  const orderedMessages = useMemo(() => {
    const sorted = [...(data?.getTicketMessages?.messages || [])].sort(
      (a, b) => {
        const aTime =
          Number(a.createdAt) || new Date(a.createdAt).getTime() || 0
        const bTime =
          Number(b.createdAt) || new Date(b.createdAt).getTime() || 0
        return aTime - bTime
      }
    )

    if (!ticket?.description) return sorted

    return [
      {
        // Prefixed so it cannot collide with a real message id as a React key.
        _id: `ticket-description-${ticket._id}`,
        senderType: 'user',
        content: ticket.description,
        attachments: [],
        createdAt: ticket.createdAt
      },
      ...sorted
    ]
  }, [data?.getTicketMessages?.messages, ticket])

  const scrollToLatestMessage = useCallback((behavior = 'auto') => {
    if (messagesContainerRef.current) {
      messagesContainerRef.current.scrollTop =
        messagesContainerRef.current.scrollHeight
    }
    messagesEndRef.current?.scrollIntoView({
      behavior,
      block: 'end',
      inline: 'nearest'
    })
  }, [])

  useEffect(() => {
    if (!open || loading) return

    const timer = setTimeout(() => {
      scrollToLatestMessage('auto')
    }, 0)
    const rafId = requestAnimationFrame(() => {
      scrollToLatestMessage('auto')
    })
    const delayedTimer = setTimeout(() => {
      scrollToLatestMessage('auto')
    }, 180)

    return () => {
      clearTimeout(timer)
      cancelAnimationFrame(rafId)
      clearTimeout(delayedTimer)
    }
  }, [open, ticketId, loading, orderedMessages.length, scrollToLatestMessage])

  const uploadAttachmentToCloudinary = async file => {
    if (!CLOUDINARY_UPLOAD_URL || !CLOUDINARY_FOOD) {
      throw new Error('Cloudinary configuration is missing')
    }
    const fileBase64 = await fileToBase64(file)
    const apiUrl = `${CLOUDINARY_UPLOAD_URL.replace(/\/$/, '')}/image/upload`

    const result = await fetch(apiUrl, {
      body: JSON.stringify({
        file: fileBase64,
        upload_preset: CLOUDINARY_FOOD
      }),
      headers: {
        'Content-Type': 'application/json'
      },
      method: 'POST'
    })

    if (!result.ok) {
      throw new Error('Attachment upload failed')
    }

    const uploaded = await result.json()
    if (!uploaded?.secure_url) {
      throw new Error('No attachment URL returned')
    }

    return {
      name: file.name,
      url: uploaded.secure_url
    }
  }

  const handleSelectAttachments = async event => {
    const files = Array.from(event.target.files || [])
    if (!files.length) return

    setUploadError('')
    setUploadingAttachments(true)
    try {
      const allowedFiles = files.slice(0, 5)
      const uploadedFiles = await Promise.all(
        allowedFiles.map(file => uploadAttachmentToCloudinary(file))
      )
      setAttachments(prev => [...prev, ...uploadedFiles].slice(0, 5))
    } catch (error) {
      console.error(error)
      setUploadError(
        error.message || 'Failed to upload attachments. Please try again.'
      )
    } finally {
      setUploadingAttachments(false)
      if (fileInputRef.current) fileInputRef.current.value = ''
    }
  }

  const removeAttachment = fileUrl => {
    setAttachments(prev => prev.filter(file => file.url !== fileUrl))
  }

  const handleSend = async () => {
    if (!message.trim() && attachments.length === 0) return
    try {
      await mutateMessage({
        variables: {
          messageInput: {
            content: message.trim() || null,
            attachments: attachments.map(file => file.url),
            ticket: ticketId
          }
        }
      })
      setMessage('')
      setAttachments([])
      setUploadError('')
      refetch()
    } catch (e) {
      console.error(e)
    }
  }

  const handleStatusChange = async event => {
    try {
      await mutateStatus({
        variables: {
          input: {
            ticketId: ticketId,
            status: event.target.value
          }
        }
      })
      refetch()
    } catch (e) {
      console.error(e)
    }
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      TransitionProps={{
        onEntered: () => scrollToLatestMessage('auto')
      }}
      PaperProps={{
        sx: { borderRadius: 4, height: '80vh' }
      }}>
      <DialogTitle
        sx={{
          p: 2,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          bgcolor: theme.palette.grey[50]
        }}>
        <Box>
          <Typography
            variant="h6"
            fontWeight="bold"
            sx={{ color: theme.palette.common.black }}>
            {ticket?.title || 'Chat'}
          </Typography>
          {ticket?.orderId && (
            <Typography
              variant="caption"
              sx={{
                display: 'block',
                mt: 0.25,
                color: theme.palette.text.secondary,
                fontWeight: 600
              }}>
              Order ID: {ticket.orderId}
            </Typography>
          )}
          <Box
            sx={{
              mt: 0.5,
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              flexWrap: 'wrap'
            }}>
            <Typography
              variant="caption"
              sx={{ color: theme.palette.text.secondary }}>
              User: {ticket?.user?.name || 'N/A'}
            </Typography>
            <Chip
              icon={<MailOutlineIcon fontSize="small" />}
              label={ticket?.user?.email || 'No email'}
              size="small"
              sx={{
                color: theme.palette.text.secondary,
                border: 'none',
                backgroundColor: 'transparent',
                '& .MuiChip-label': {
                  color: theme.palette.text.secondary,
                  fontSize: theme.typography.caption.fontSize,
                  fontWeight: theme.typography.caption.fontWeight,
                  lineHeight: theme.typography.caption.lineHeight
                }
              }}
            />
            <Chip
              icon={<PhoneIcon fontSize="small" />}
              label={ticket?.user?.phone || 'No phone'}
              size="small"
              sx={{
                color: theme.palette.text.secondary,
                border: 'none',
                backgroundColor: 'transparent',
                '& .MuiChip-label': {
                  color: theme.palette.text.secondary,
                  fontSize: theme.typography.caption.fontSize,
                  fontWeight: theme.typography.caption.fontWeight,
                  lineHeight: theme.typography.caption.lineHeight
                }
              }}
            />
          </Box>
        </Box>
        <Box display="flex" alignItems="center">
          {ticket && (
            <>
              <Typography
                variant="body2"
                sx={{
                  mr: 1,
                  color: theme.palette.common.black,
                  fontWeight: 'bold'
                }}>
                Status:
              </Typography>
              <Select
                value={ticket.status}
                onChange={handleStatusChange}
                size="small"
                sx={{
                  mr: 2,
                  height: 32,
                  fontSize: '0.8rem',
                  color: theme.palette.common.black,
                  '& .MuiSelect-select': {
                    color: theme.palette.common.black
                  }
                }}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      '& .MuiMenuItem-root': {
                        color: theme.palette.common.black
                      }
                    }
                  }
                }}>
                <MenuItem value={SUPPORT_TICKET_STATUS.OPEN}>Open</MenuItem>
                <MenuItem value={SUPPORT_TICKET_STATUS.IN_PROGRESS}>
                  In Progress
                </MenuItem>
                <MenuItem value={SUPPORT_TICKET_STATUS.CLOSED}>Closed</MenuItem>
              </Select>
            </>
          )}
          <IconButton onClick={onClose} size="small">
            <CloseIcon sx={{ color: theme.palette.common.black }} />
          </IconButton>
        </Box>
      </DialogTitle>
      <Divider />
      <DialogContent
        sx={{
          p: 2,
          display: 'flex',
          flexDirection: 'column',
          bgcolor: '#fbfbfb'
        }}>
        {loading && !data ? (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            flex={1}>
            <CustomLoader />
          </Box>
        ) : (
          <Box
            ref={messagesContainerRef}
            sx={{ flex: 1, overflowY: 'auto', px: 1 }}>
            {orderedMessages.map(msg => (
              <ChatMessage
                key={msg._id}
                message={msg}
                isMe={(msg.senderType || '').toLowerCase() === 'admin'}
              />
            ))}
            <div ref={messagesEndRef} />
          </Box>
        )}
      </DialogContent>
      <Divider />
      <DialogActions sx={{ p: 2, bgcolor: theme.palette.common.white }}>
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept="image/*"
          hidden
          onChange={handleSelectAttachments}
        />
        <Box sx={{ width: '100%' }}>
          {attachments.length > 0 && (
            <Box
              sx={{
                mb: 1.25,
                display: 'flex',
                flexWrap: 'wrap',
                gap: 1
              }}>
              {attachments.map(file => (
                <Chip
                  key={file.url}
                  label={file.name}
                  onDelete={() => removeAttachment(file.url)}
                  sx={{
                    maxWidth: 240,
                    borderRadius: '18px',
                    height: 36,
                    border: `1px solid ${theme.palette.grey[300]}`,
                    bgcolor: theme.palette.common.white,
                    color: theme.palette.common.black,
                    fontWeight: 500,
                    '& .MuiChip-label': {
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      color: theme.palette.common.black
                    },
                    '& .MuiChip-deleteIcon': {
                      color: theme.palette.grey[700],
                      '&:hover': {
                        color: theme.palette.error.main
                      }
                    }
                  }}
                />
              ))}
            </Box>
          )}
          {uploadError && (
            <Typography
              variant="caption"
              sx={{ color: theme.palette.error.main, display: 'block', mb: 1 }}>
              {uploadError}
            </Typography>
          )}
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <IconButton
              onClick={() => fileInputRef.current?.click()}
              disabled={ticket?.status === SUPPORT_TICKET_STATUS.CLOSED}
              sx={{
                mr: 1,
                width: 48,
                height: 48,
                borderRadius: 2,
                border: `1px solid ${theme.palette.grey[300]}`,
                color: theme.palette.primary.main,
                bgcolor: theme.palette.common.white
              }}>
              <AttachFileIcon />
            </IconButton>
            <TextField
              fullWidth
              placeholder="Type your message here..."
              variant="outlined"
              size="small"
              value={message}
              onChange={e => setMessage(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault()
                  handleSend()
                }
              }}
              disabled={ticket?.status === SUPPORT_TICKET_STATUS.CLOSED}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 3,
                  color: theme.palette.common.black,
                  minHeight: 48
                },
                '& .MuiInputBase-input': {
                  color: theme.palette.common.black
                }
              }}
            />
            <Button
              variant="contained"
              color="primary"
              onClick={handleSend}
              disabled={
                (!message.trim() && attachments.length === 0) ||
                ticket?.status === SUPPORT_TICKET_STATUS.CLOSED ||
                uploadingAttachments
              }
              sx={{
                ml: 1.5,
                borderRadius: 3,
                minWidth: 120,
                height: 48,
                fontWeight: 'bold',
                fontSize: '1.1rem'
              }}>
              Send
            </Button>
          </Box>
        </Box>
      </DialogActions>
    </Dialog>
  )
}

export default TicketChatModal
